import type { TypedDocumentNode } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import {
  initialPagedGridInput,
  PAGED_LIST_FETCH_POLICY,
  type PagedGridQueryState,
  usePreloadedServerGridQuery,
} from 'src/modules/grids';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import type { CustomerGroupPageQuery, CustomerGroupPageQueryVariables } from 'types/graphql';
import { useStableRows } from '../../useStableRows';
import type { CustomerGroupsMeta } from '../constants';

/** Where the Customer Groups grid persists its layout. v1 held LOCAL sort/filter state on a computed column. */
export const CUSTOMER_GROUPS_GRID_STORAGE_KEY = 'customer-groups_v2';

/** The Customer Groups grid's bounded read (TAS-3660 P7 BZ75): one server page per request. */
export const CUSTOMER_GROUP_PAGE_QUERY: TypedDocumentNode<CustomerGroupPageQuery, CustomerGroupPageQueryVariables> =
  gql`
  query CustomerGroupPageQuery($input: StandardPageInput!) {
    customerGroupPage(input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          enabled
          name
          updatedAt
          customers {
            id
            name
          }
        }
      }
    }
  }
`;

type GroupNode = NonNullable<
  NonNullable<NonNullable<CustomerGroupPageQuery['customerGroupPage']['edges']>[number]>['node']
>;
type GroupCustomer = GroupNode['customers'][number];

const isCustomer = (customer: GroupCustomer): customer is NonNullable<GroupCustomer> => customer != null;
// One grid row per page node, kept across re-renders: export checks the rows on screen stay the same objects.
const groupByNode = new WeakMap<GroupNode, CustomerGroupsMeta>();
const toGroup = (node: GroupNode): CustomerGroupsMeta => {
  const known = groupByNode.get(node);
  if (known) return known;
  const group = { ...node, customers: node.customers.filter(isCustomer) };
  groupByNode.set(node, group);
  return group;
};

export default function useCustomerGroups(gridState: PagedGridQueryState) {
  const page = usePreloadedServerGridQuery({
    queryKey: 'customerGroupPage',
    query: CUSTOMER_GROUP_PAGE_QUERY,
    gridState,
    label: 'customer groups',
  });
  const { rows, fetchAllForExport } = page;
  const groups = useStableRows(useMemo(() => rows.map(toGroup), [rows]));
  // Export walks every page under the grid's filters (#7627 F20) and shapes each group as the grid shows it.
  const exportAllGroups = useCallback(async () => (await fetchAllForExport()).map(toGroup), [fetchAllForExport]);
  return { ...page, groups, fetchAllForExport: exportAllGroups };
}

/** The grid's first page as it will ask for it; the Customer Groups tab preloads it on intent. */
export function preloadCustomerGroupsGrid(context: AppRouterContext) {
  const input = initialPagedGridInput(CUSTOMER_GROUPS_GRID_STORAGE_KEY);
  return preloadRouteQuery(context, CUSTOMER_GROUP_PAGE_QUERY, { input }, { fetchPolicy: PAGED_LIST_FETCH_POLICY });
}
